import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import { ComposeContext, draftProblem, errorMessage } from "./composerAdapters";
import { useEmailComposer } from "./useEmailComposer";
import RecipientField from "./RecipientField";
import ComposeAttachments from "./ComposeAttachments";
import ComposerContextPanel from "./ComposerContextPanel";
import EmailCard from "../EmailCard";

const EmailComposerForm = ({ context }: { context: ComposeContext }) => {
    const { t } = useOfficeTranslation();
    const navigate = useNavigate();
    const composer = useEmailComposer(context);
    const { draft, loading, loadError, sending, sendError, patch } = composer;
    const [ccExpanded, setCcExpanded] = useState(false);
    const [originalOpen, setOriginalOpen] = useState(false);
    const problem = draftProblem(draft);
    const ccVisible = ccExpanded || draft.cc.length > 0 || Boolean(draft.ccInput);
    if (loading || loadError) return <div className="card composer-loading" aria-live="polite">
        {loading ? <p role="status">{t("email.composer.loading")}</p> : <>
            <p role="alert">{t("email.composer.loadingFailed")}</p>
            <button type="button" className="btn btn-primary" onClick={composer.retryLoading}>{t("email.composer.retry")}</button>
        </>}
    </div>;

    return <>
        <form className="card email-composer" onSubmit={async event => {
            event.preventDefault();
            const sent = await composer.submit();
            if (sent) navigate(`/conversation/${sent.id}`, { state: { composed: true } });
        }}>
            <div className="composer-header"><h2>{context.replyId ? t("email.composer.reply") : t("email.composer.newMessage")}</h2></div>
            <div className="composer-layout">
                <div className="composer-main">
                    {draft.type !== "whatsapp" && <>
                        <div className="composer-field composer-to">
                            <div className="composer-label-row">
                                <label htmlFor="composer-to" className="form-label">{t("common.to")}</label>
                                {!ccVisible && <button type="button" className="btn btn-link" disabled={sending} onClick={() => setCcExpanded(true)}>{t("email.composer.addCc")}</button>}
                            </div>
                            <RecipientField id="composer-to" label={t("common.to")} value={draft.to ? [draft.to] : []} input={draft.toInput} contacts={composer.contacts} disabled={sending}
                                onChange={values => composer.selectTo(values[0] ?? null)} onInputChange={value => patch({ toInput: value })} />
                        </div>
                        {ccVisible && <div className="composer-field">
                            <label htmlFor="composer-cc" className="form-label">{t("common.cc")}</label>
                            <RecipientField id="composer-cc" label={t("common.cc")} multiple value={draft.cc} input={draft.ccInput} contacts={composer.contacts} disabled={sending} excludedEmail={draft.to?.email}
                                onChange={composer.selectCc} onInputChange={value => patch({ ccInput: value })} />
                        </div>}
                        <div className="composer-field">
                            <label htmlFor="composer-subject" className="form-label">{t("common.subject")}</label>
                            <input id="composer-subject" className="form-control" type="text" value={draft.subject} disabled={sending} onChange={event => patch({ subject: event.target.value })} onKeyDown={event => { if (event.key === "Enter") event.preventDefault(); }} />
                        </div>
                    </>}
                    <div className="composer-field composer-message">
                        <label htmlFor="composer-body" className="form-label">{t("common.body")}</label>
                        <textarea id="composer-body" className="form-control" rows={10} value={draft.body} disabled={sending} onChange={event => patch({ body: event.target.value })} />
                    </div>
                    {draft.type !== "whatsapp" && <ComposeAttachments composer={composer} />}
                </div>
                <ComposerContextPanel composer={composer} originalOpen={originalOpen} onToggleOriginal={() => setOriginalOpen(value => !value)} />
            </div>
            <div className="composer-footer">
                <div className="composer-status" id="composer-status" aria-live="polite">
                    {sendError ? <div className="text-danger" role="alert"><strong>{t("email.composer.sendFailed")}</strong><span>{errorMessage(sendError)}</span></div>
                        : sending ? t("email.composer.sending") : problem ? t(`email.composer.${problem}`) : t("email.composer.ready")}
                </div>
                <button type="submit" className="btn btn-primary composer-send" disabled={sending || Boolean(problem)} aria-describedby="composer-status">
                    <i className={`mdi ${sending ? "mdi-loading mdi-spin" : "mdi-send"} me-1`} aria-hidden="true" />{sending ? t("email.composer.sending") : t("common.send")}
                </button>
            </div>
        </form>
        {composer.reply && originalOpen && <section id="composer-original" className="composer-original" aria-label={t("email.composer.originalMessage")}>
            <EmailCard id={composer.reply.id} withoutReplies withoutFollowUps />
        </section>}
    </>;
};

export default EmailComposerForm;
